import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faList,
  faChartPie,
  faSearch,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function MyLimitHome() {
  const [activeTab, setActiveTab] = useState("entry");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [limits, setLimits] = useState([
    {
      id: 1,
      title: "Daily Focus Time",
      aspect: "Time",
      period: "Daily",
      capacity: 6,
      consumed: 4.5,
      unit: "Hours",
      keywords: "work focus coding",
      notes: "Deep work sessions",
    },
    {
      id: 2,
      title: "Monthly Spend",
      aspect: "Money",
      period: "Monthly",
      capacity: 50000,
      consumed: 32000,
      unit: "INR",
      keywords: "expense budget",
      notes: "Ops + SaaS",
    },
  ]);

  const [form, setForm] = useState({
    title: "",
    aspect: "Time",
    period: "Daily",
    capacity: "",
    consumed: "",
    unit: "",
    keywords: "",
    notes: "",
  });

  const handleAdd = () => {
    if (!form.title || !form.capacity) return;
    setLimits([
      ...limits,
      { ...form, id: Date.now(), consumed: Number(form.consumed || 0) },
    ]);
    setForm({
      title: "",
      aspect: "Time",
      period: "Daily",
      capacity: "",
      consumed: "",
      unit: "",
      keywords: "",
      notes: "",
    });
    setActiveTab("list");
  };

  const filtered = limits.filter(
    (l) =>
      l.title.toLowerCase().includes(search.toLowerCase()) ||
      l.keywords.toLowerCase().includes(search.toLowerCase())
  );

  const chartData = (item) => ({
    labels: ["Consumed", "Remaining"],
    datasets: [
      {
        data: [
          item.consumed,
          Math.max(item.capacity - item.consumed, 0),
        ],
        backgroundColor: ["#14b8a6", "#064e3b"],
        borderWidth: 0,
      },
    ],
  });

  return (
    <div className="min-h-screen bg-black text-teal-300 p-3">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col mt-b">
          <div className="text-lg lg:text-2xl font-bold text-yellow-200">My Limit</div>
          <div className="text-xs lg:text-lg text-gray-200">What I Can Do and Have Done!</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="mt-4 p-2 rounded-md bg-red-600 hover:bg-red-700 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-3 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
              >
                Login
              </button>
            </>
          )}
        </div>
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* Mobile menu content */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-xs lg:text-sm font-medium">{userName}</div>
              <div className="flex gap-3">
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md bg-red-600 hover:bg-red-700"
                >
                  ⏻
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-center gap-6">
              <Link to="/login" className="text-teal-300">Login</Link>
            </div>
          )}
        </div>
      )}  

      {/* Tabs */}
      <div className="grid grid-cols-3 gap-2 mb-2 mt-2 text-xs">
        <Tab icon={faPlus} label="New Entry" active={activeTab==="entry"} onClick={()=>setActiveTab("entry")} />
        <Tab icon={faList} label="List" active={activeTab==="list"} onClick={()=>setActiveTab("list")} />
        <Tab icon={faChartPie} label="Analysis" active={activeTab==="analysis"} onClick={()=>setActiveTab("analysis")} />
      </div>

      {/* ENTRY */}
      {activeTab === "entry" && (
        <div className="space-y-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm text-teal-300">Title</label>
            <input
              className="w-80 h-9 rounded-md px-2 text-black"
              value={form.title}
              onChange={(e) =>
                setForm({ ...form, title: e.target.value })
              }
              placeholder="Enter title"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-sm text-teal-300">Aspect</label>
              <select
                className="w-40 h-9 rounded-md px-2 text-black"
                value={form.aspect}
                onChange={(e) =>
                  setForm({ ...form, aspect: e.target.value })
                }
              >
                <option>Time</option>
                <option>Money</option>
                <option>Work Output</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm text-teal-300">Period</label>
              <select
                className="w-40 h-9 rounded-md px-2 text-black"
                value={form.period}
                onChange={(e) =>
                  setForm({ ...form, period: e.target.value })
                }
              >
                <option>Daily</option>
                <option>Weekly</option>
                <option>Monthly</option>
                <option>Custom</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-sm text-teal-300">Capacity</label>
              <input
                type="number"
                className="w-40 h-9 rounded-md px-2 text-black"
                value={form.capacity}
                onChange={(e) =>
                  setForm({ ...form, capacity: e.target.value })
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm text-teal-300">Consumed</label>
              <input
                type="number"
                className="w-40 h-9 rounded-md px-2 text-black"
                value={form.consumed}
                onChange={(e) =>
                  setForm({ ...form, consumed: e.target.value })
                }
              />
            </div>
          </div>
          <div className="flex flex-col gap-1 mb-2">
            <label className="text-sm text-teal-300">Brief Details</label>
            <textarea
              style={{height: "150px"}}
              className="w-full rounded-md px-2 py-2 text-white"
              value={form.notes}
              onChange={(e) =>
                setForm({ ...form, notes: e.target.value })
              }
              placeholder="Explain context, assumptions, notes…"
            />
          </div>
          <button
            onClick={handleAdd}
            className="w-full bg-teal-600 hover:bg-teal-500 text-white py-2 rounded-md font-semibold transition"
          >
            Save Limit
          </button>

        </div>
      )}

      {/* LIST */}
      {activeTab === "list" && (
        <div>
          <div className="flex items-center mb-3 bg-gray-900 rounded px-2">
            <FontAwesomeIcon icon={faSearch} />
            <input
              className="bg-transparent p-2 w-full outline-none text-sm"
              placeholder="Search by title or keyword"
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            {filtered.map((l) => {
              const pct = Math.round((l.consumed / l.capacity) * 100);
              return (
                <div
                  key={l.id}
                  onClick={()=>setSelected(l)}
                  className="bg-gray-900 p-3 rounded-md cursor-pointer"
                >
                  <div className="flex justify-between">
                    <div>
                      <div className="font-semibold">{l.title}</div>
                      <div className="text-xs text-teal-400">
                        {l.aspect} · {l.period}
                      </div>
                    </div>
                    <div className={`text-xs ${pct>90?"text-red-400":pct>70?"text-yellow-400":"text-teal-400"}`}>
                      {pct}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* DETAIL */}
          {selected && (
            <div className="mt-4 bg-gray-950 p-3 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faCircleInfo} />
                <span className="font-semibold">{selected.title}</span>
              </div>
              <div className="text-xs space-y-1">
                <div>Capacity: {selected.capacity} {selected.unit}</div>
                <div>Consumed: {selected.consumed}</div>
                <div>Keywords: {selected.keywords}</div>
                <div>Notes: {selected.notes}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ANALYSIS */}
      {activeTab === "analysis" && (
        <div className="grid grid-cols-1 gap-4">
          {limits.map((l) => (
            <div key={l.id} className="bg-gray-900 p-3 rounded-md">
              <div className="text-sm mb-2">{l.title}</div>
              <Doughnut data={chartData(l)} />
            </div>
          ))}
        </div>
      )}
      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-2">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm text-white">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>      
    </div>
  );
}

/* ---------- Reusable UI ---------- */

const Tab = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex flex-row items-center justify-center text-center py-2 rounded-md ${
      active ? "bg-teal-600 text-white" : "bg-gray-900"
    }`}
  >
    <FontAwesomeIcon icon={icon} />
    <span className="mt-1">{label}</span>
  </button>
);

const Input = ({ label, value, onChange, type="text" }) => (
  <div>
    <div className="text-xs mb-1">{label}</div>
    <input
      type={type}
      value={value}
      onChange={(e)=>onChange(e.target.value)}
      className="w-full bg-gray-900 p-2 rounded outline-none"
    />
  </div>
);

const Textarea = ({ label, value, onChange }) => (
  <div>
    <div className="text-xs mb-1">{label}</div>
    <textarea
      value={value}
      onChange={(e)=>onChange(e.target.value)}
      className="w-full bg-gray-900 p-2 rounded outline-none"
    />
  </div>
);

const Select = ({ label, value, options, onChange }) => (
  <div>
    <div className="text-xs mb-1">{label}</div>
    <select
      value={value}
      onChange={(e)=>onChange(e.target.value)}
      className="w-full bg-gray-900 p-2 rounded outline-none"
    >
      {options.map(o=>(
        <option key={o}>{o}</option>
      ))}
    </select>
  </div>
);
